
import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { X, Plus } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';

interface CreateNoteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onNoteCreated: () => void;
}

const NOTE_COLORS = [
  '#ffffff', // White
  '#fef3c7', // Yellow
  '#fecaca', // Red
  '#bbf7d0', // Green
  '#bfdbfe', // Blue
  '#e0e7ff', // Indigo
  '#f3e8ff', // Purple
  '#fce7f3', // Pink
];

const NOTE_CATEGORIES = [
  { value: 'general', label: 'General' },
  { value: 'ideas', label: 'Ideas' },
  { value: 'reminders', label: 'Reminders' },
];

type Board = {
  id: string;
  title: string;
};

export const CreateNoteDialog = ({ open, onOpenChange, onNoteCreated }: CreateNoteDialogProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState<'general' | 'ideas' | 'reminders'>('general');
  const [color, setColor] = useState('#ffffff');
  const [selectedBoardId, setSelectedBoardId] = useState<string>('private');
  const [boards, setBoards] = useState<Board[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState<string[]>([]);

  // Fetch user's accessible boards
  useEffect(() => {
    const fetchBoards = async () => {
      if (!user) return;

      try {
        const { data, error } = await supabase
          .from('boards')
          .select('id, title')
          .order('title');

        if (error) throw error;
        setBoards(data || []);
      } catch (error) {
        console.error('Error fetching boards:', error);
      }
    };

    if (open) {
      fetchBoards();
    }
  }, [user, open]);

  const handleAddTag = () => {
    const newTag = tagInput.trim().toLowerCase();
    if (newTag && !tags.includes(newTag)) {
      setTags([...tags, newTag]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleBoardSelection = (boardId: string) => {
    setSelectedBoardId(boardId);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user || !title.trim()) {
      toast({
        title: "Error",
        description: "Title is required",
        variant: "destructive"
      });
      return;
    }


    setLoading(true);

    try {
      const { error } = await supabase
        .from('reminder_notes')
        .insert({
          user_id: user.id,
          title: title.trim(),
          content: { type: 'doc', content: [{ type: 'paragraph', content: [{ type: 'text', text: content }] }] },
          category,
          color,
          visibility: selectedBoardId !== 'private' ? 'board_shared' : 'private',
          board_id: selectedBoardId !== 'private' ? selectedBoardId : null,
          tags,
          position: 0,
        });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Note created successfully",
      });

      // Reset form
      setTitle('');
      setContent('');
      setCategory('general');
      setColor('#ffffff');
      setSelectedBoardId('private');
      setTags([]);
      setTagInput('');
      
      onNoteCreated();
      onOpenChange(false);
    } catch (error) {
      console.error('Error creating note:', error);
      toast({
        title: "Error",
        description: "Failed to create note",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create New Note</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Title */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Title *</label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter note title..."
              required
            />
          </div>

          {/* Content */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Content</label>
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Write your note content..."
              rows={6}
            />
          </div>

          {/* Board Selection */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Share to Board</label>
              <Select value={selectedBoardId} onValueChange={handleBoardSelection}>
                <SelectTrigger>
                  <SelectValue placeholder="Private note" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="private">Private note</SelectItem>
                  {boards.map((board) => (
                    <SelectItem key={board.id} value={board.id}>
                      {board.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Category</label>
              <Select value={category} onValueChange={(value: any) => setCategory(value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {NOTE_CATEGORIES.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Color */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Color</label>
            <div className="flex gap-2 flex-wrap">
              {NOTE_COLORS.map((noteColor) => (
                <button
                  key={noteColor}
                  type="button"
                  className={`w-8 h-8 rounded-full border-2 transition-all ${
                    color === noteColor ? 'border-foreground scale-110' : 'border-border hover:scale-105'
                  }`}
                  style={{ backgroundColor: noteColor }}
                  onClick={() => setColor(noteColor)}
                />
              ))}
            </div>
          </div>

          {/* Tags */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Tags</label>
            <div className="flex gap-2 flex-wrap mb-2">
              {tags.map((tag) => (
                <Badge key={tag} variant="secondary" className="flex items-center gap-1">
                  {tag}
                  <button
                    type="button"
                    onClick={() => handleRemoveTag(tag)}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Add a tag..."
                className="flex-1"
              />
              <Button type="button" variant="outline" size="sm" onClick={handleAddTag}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Context Info */}
          {selectedBoardId !== 'private' && (
            <div className="p-3 bg-muted rounded-lg text-sm">
              <p className="font-medium">Note Context:</p>
              <p className="text-muted-foreground">
                This note will be shared with all members of the selected board.
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading || !title.trim()}>
              {loading ? 'Creating...' : 'Create Note'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
